'use client';

import { cn } from '@/lib/ui/cn';
import type { BlockCarouselProps } from '@/types/blocks';
import { cva } from 'class-variance-authority';
import useEmblaCarousel from 'embla-carousel-react';
import * as React from 'react';

const itemWrapperVariants = cva('flex', {
  variants: {
    gap: {
      none: 'space-x-0',
      small: 'space-x-1',
      medium: 'space-x-2',
      large: 'space-x-4',
    },
  },
});

const carouselItemVariants = cva('shrink-0 flex', {
  variants: {
    itemWidth: {
      full: 'basis-full min-h-[300px] lg:min-h-[400px]',
      half: 'basis-full lg:basis-1/2 min-h-[280px] lg:min-h-[350px]',
      third: 'basis-full xl:basis-1/3 min-h-[250px] xl:min-h-[300px]',
      quarter: 'basis-full lg:basis-1/4 min-h-[220px] lg:min-h-[280px]',
      fifth: 'basis-full lg:basis-1/5 min-h-[200px] lg:min-h-[250px]',
      sixth: 'basis-full lg:basis-1/6 min-h-[180px] lg:min-h-[220px]',
      auto: 'basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/5 xl:basis-1/6 min-h-[160px] lg:min-h-[200px]',
    },
  },
});

export function Carousel({
  itemWidth = 'third',
  showControls = false,
  gap = 'medium',
  className,
  children,
  peekNextItem = false,
  showPaginationAtDesktopViewport = true,
}: BlockCarouselProps) {
  const [emblaRef, embla] = useEmblaCarousel({
    align: 'start',
    containScroll: peekNextItem ? 'keepSnaps' : 'trimSnaps',
  });
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);
  const childrenCount = React.Children.count(children);

  React.useEffect(() => {
    if (!embla) return;

    const updateAll = () => {
      setScrollSnaps(embla.scrollSnapList());
      setSelectedIndex(embla.selectedScrollSnap());
    };
    const onSelect = () => setSelectedIndex(embla.selectedScrollSnap());

    // run immediately once embla is ready
    updateAll();

    // keep in sync when layout/images cause a re-measure
    embla.on('reInit', updateAll);
    embla.on('init', updateAll);
    embla.on('select', onSelect);

    return () => {
      embla.off('reInit', updateAll);
      embla.off('init', updateAll);
      embla.off('select', onSelect);
    };
    // re-run if slide count changes
  }, [embla, childrenCount]);

  return (
    <section
      className={cn(
        'size-full',
        peekNextItem ? 'overflow-visible' : 'overflow-hidden',
        className
      )}
      data-component='carousel'
      data-test='carousel'
    >
      <div className={cn('relative')}>
        <div ref={emblaRef}>
          <div className={itemWrapperVariants({ gap })}>
            {React.Children.map(children, child => (
              <div
                className={cn(
                  carouselItemVariants({ itemWidth }),
                  peekNextItem && 'max-w-[95%] lg:max-w-[70%]'
                )}
              >
                <div className='flex w-full flex-col select-none'>{child}</div>
              </div>
            ))}
          </div>
        </div>
        {showControls && scrollSnaps.length > 1 && (
          <div
            className={cn(
              'flex justify-center gap-2 py-4',
              showPaginationAtDesktopViewport &&
                'xl:absolute xl:inset-17.5 xl:top-auto'
            )}
          >
            {scrollSnaps.map((_, idx) => (
              <button
                key={idx}
                className={cn(
                  'size-4 rounded-full transition-colors',
                  idx === selectedIndex ? 'bg-sh-dark-gray' : 'bg-sh-gray'
                )}
                onClick={() => embla?.scrollTo(idx)}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
